import { graphql } from 'gatsby';
import React from 'react';
import Seo from '../../components/SEO';
import CallToActions from '../../components/CallToActions/CallToActions';
import ContentBlock from '../../components/ContentBlock/ContentBlock';
import Cover, { CoverHeading, CoverLead } from '../../components/Cover/Cover';
import FeaturedTestimonials from '../../components/FeaturedTestimonials/FeaturedTestimonials';
import HeaderTheme from '../../components/Header/HeaderTheme';
import LocationsBanner from '../../components/LocationsBanner/LocationsBanner';
import Promotions from '../../components/Promotions/Promotions';
import RecentPosts from '../../components/RecentPosts/RecentPosts';
import Services from '../../components/Services/Services';
import BackLink from '../../components/UI/BackLink/BackLink';
import Navbar from '../../components/UI/Navbar/Navbar';
import Teaser from '../../components/UI/Teaser/Teaser';
import usePromotions from '../../hooks/usePromotions';

const ServiceCategoryTemplate = ({
  data: {
    wp: { seo },
    page: { 
      name, 
      description, 
      category, 
      sectionContent,
      sectionAside,
      sectionCtas,
      sectionPromotions
    },
    services
  },
  location: { pathname }
}) => {
  const { heading, image } = category;
  const navigation = [
    { label: 'Overview', path: '#overview' },
    { label: 'Services', path: '#services' },
    { label: 'Locations', path: '#locations' },
    { label: 'Testimonials', path: '#testimonials' },
  ];
  const promotions = usePromotions(sectionPromotions.promotions, services.nodes, {
    excludePath: pathname,
    limit: 4
  });

  return (
    <>
      <HeaderTheme isReverse={!!image}/>

      <Seo
          title={`${name} | Services`}
          description={description}
          image={image}
          postSchema={JSON.parse(
            seo.contentTypes.service.schema.raw
          )}
      />

      <Cover image={image} hasSpacer>
        <Teaser>Our services</Teaser>
        <CoverHeading>{heading}</CoverHeading>
        <CoverLead>{description}</CoverLead>
      </Cover>

      <Navbar nodes={navigation} />
      <ContentBlock
        heading="Overview"
        body={sectionContent.body}
        aside={sectionAside.aside}
        id="overview"
      />
      <Services nodes={services.nodes} isInnerOffsetY={false} />
      <CallToActions items={sectionCtas.ctas} />
      <LocationsBanner filterByService />
      <Promotions items={promotions} />
      <RecentPosts />
      <FeaturedTestimonials showAsCarousel />
      <BackLink label="services" path="/services"/>
    </>
  );
};

export default ServiceCategoryTemplate;

export const query = graphql`
  query serviceCategory($id: String!) {
    wp {
      seo {
        contentTypes {
          service {
            schema {
              raw
            }
          }
        }
      }
    }

    page: wpServiceCategory(id: { eq: $id }) {
      id
      name
      description
      category {
        heading
        image {
          localFile {
            ...HeroImage
          }
        }
      }

      sectionContent {
        body {
          ... on WpServiceCategory_Sectioncontent_Body_LayoutWysiwyg {
            fieldGroupName
            wysiwyg
          }
          ... on WpServiceCategory_Sectioncontent_Body_LayoutImage {
            fieldGroupName
            image {
              title
              ...WpImage
            }
          }
          ... on WpServiceCategory_Sectioncontent_Body_LayoutVideo {
            fieldGroupName
            video
            caption
          }
        }
      }

      sectionAside {
        aside {
          ... on WpServiceCategory_Sectionaside_Aside_LayoutVideo {
            fieldGroupName
            video
            caption
          }
          ... on WpServiceCategory_Sectionaside_Aside_LayoutImage {
            fieldGroupName
            image {
              title
              ...WpImage
            }
          }
          ... on WpServiceCategory_Sectionaside_Aside_LayoutTestimonial {
            fieldGroupName
            testimonial {
              ...Testimonial
            }
          }
          ... on WpServiceCategory_Sectionaside_Aside_LayoutNavigationCard {
            fieldGroupName
            title
            description
            image {
              localFile {
                ...HalfImage
              }
            }
            link {
              url
              title
              target
            }
          }
        }
      }

      sectionCtas {
        ctas {
          heading
          description
          link {
            target
            title
            url
          }
        }
      }

      sectionPromotions {
        promotions {
          promotion {
            ...Promotion
          }
        }
      }
    }

    services: allWpService(
      filter: {servicesCategory: {nodes: {elemMatch: {id: {eq: $id}}}}}
      sort: {title: DESC}
    ) {
      nodes {
        ...ServiceCard
      }
    }
  }
`;
